import { SHORTCUT_LABEL } from '../keyboard';
import type { VoiceState } from '../voice/VoiceSession';
import { LockIcon, MegaphoneIcon, MicIcon, MicOffIcon, ScreenIcon, WhisperIcon } from './icons';
import { LevelBar } from './LevelBar';
import type { RoomContext } from './RoomView';

type Mode = 'public' | 'whisper' | 'announce' | 'muted' | 'waiting';

/** Phones and tablets have no Space key, so hints talk about buttons instead. */
const touchOnly = typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches && !matchMedia('(any-pointer: fine)').matches;

/**
 * The always-visible answer to "who can hear me right now?".
 * The whole bar takes your team's colour while you whisper.
 */
export function VoiceBar({ ctx, state: v }: { ctx: RoomContext; state: VoiceState }) {
  const { quiz, me, isHost, voice } = ctx;
  const team = quiz.teams.find((t) => t.id === me?.teamId);
  const whisperOn = v.whisperHeld || v.whisperLocked;
  const announceOn = v.announceHeld || v.announceLocked;

  let mode: Mode;
  let title: string;
  let detail: string;

  if (v.phase !== 'connected') {
    mode = 'waiting';
    title = v.phase === 'reconnecting' ? 'Reconnecting…' : 'Connecting…';
    detail = 'Your voice will come back on its own.';
  } else if (v.micMuted) {
    mode = 'muted';
    title = 'You are muted';
    detail = touchOnly ? 'Tap Unmute to talk.' : `Press ${SHORTCUT_LABEL.mute} to unmute.`;
  } else if (isHost) {
    if (announceOn) {
      mode = 'announce';
      title = 'Announcing to everyone';
      detail = v.announceLocked ? 'Click Stop announcing when you are done.' : `Release ${SHORTCUT_LABEL.announce} to stop.`;
    } else {
      mode = 'public';
      title = 'Everyone can hear you';
      detail = touchOnly ? 'Tap Announce to talk over the huddles.' : `Hold ${SHORTCUT_LABEL.announce} to announce over the huddles.`;
    }
  } else if (whisperOn) {
    if (v.route === 'team') {
      mode = 'whisper';
      title = `Only ${team?.name ?? 'your team'} can hear you`;
      detail = v.whisperLocked ? 'Click Stop whispering to talk to everyone.' : `Release ${SHORTCUT_LABEL.whisper} to talk to everyone.`;
    } else {
      mode = 'waiting';
      title = team ? `Connecting to ${team.name}…` : 'You are not on a team yet';
      detail = 'Nobody can hear you until this finishes.';
    }
  } else {
    mode = 'public';
    title = 'Everyone can hear you';
    detail = !team
      ? 'The quizmaster will put you on a team.'
      : touchOnly
        ? `Tap Whisper to talk only to ${team.name}.`
        : `Hold ${SHORTCUT_LABEL.whisper} to whisper to ${team.name}.`;
  }

  return (
    <footer className="voicebar" data-mode={mode} aria-live="polite">
      <div className="voice-status">
        <span className="voice-icon" aria-hidden>
          {mode === 'muted' ? <MicOffIcon /> : mode === 'whisper' ? <WhisperIcon /> : mode === 'announce' ? <MegaphoneIcon /> : <MicIcon />}
        </span>
        <div>
          <p className="voice-title">{title}</p>
          <p className="voice-detail">
            {detail}
            {v.teamDucked && !isHost && <span className="ducked"> Room turned down while your team talks.</span>}
          </p>
        </div>
        {!v.micMuted && <LevelBar db={mode === 'waiting' ? -120 : v.micLevelDb} label="Your microphone level" />}
      </div>

      <div className="voice-actions">
        {isHost ? (
          <>
            <button className="btn btn-announce" aria-pressed={v.announceLocked} onClick={() => voice.toggleAnnounceLock()} disabled={v.micMuted}>
              <MegaphoneIcon /> {v.announceLocked ? 'Stop announcing' : 'Announce'}
              <kbd>{SHORTCUT_LABEL.announce}</kbd>
            </button>
            <button
              className="btn"
              aria-pressed={Boolean(v.screen?.isLocal)}
              onClick={() => void (v.screen?.isLocal ? voice.stopScreenShare() : voice.startScreenShare())}
            >
              <ScreenIcon /> {v.screen?.isLocal ? 'Stop sharing' : 'Share screen'}
            </button>
          </>
        ) : (
          <button
            className="btn btn-whisper"
            aria-pressed={v.whisperLocked}
            onClick={() => voice.toggleWhisperLock()}
            disabled={!team || v.micMuted}
            title="Click to keep whispering; or hold Space"
          >
            {v.whisperLocked ? <LockIcon /> : <WhisperIcon />} {v.whisperLocked ? 'Stop whispering' : 'Whisper to team'}
            <kbd>{SHORTCUT_LABEL.whisper}</kbd>
          </button>
        )}
        <button className={`btn btn-mute${v.micMuted ? ' is-muted' : ''}`} aria-pressed={v.micMuted} onClick={() => voice.toggleMute()}>
          {v.micMuted ? <MicOffIcon /> : <MicIcon />} {v.micMuted ? 'Unmute' : 'Mute'}
          <kbd>{SHORTCUT_LABEL.mute}</kbd>
        </button>
      </div>
    </footer>
  );
}
